<template>
  <div style="background-color: #fff; border-top: 0px; border-radius: 0 0 3px 3px;">
    <div class="margin-subpages talent-matrix-container">
      <!-- Header Controls -->
      <div class="header-controls">
        <div class="header-row header-row-primary">
          <div class="header-left">
            <el-select
              v-model="localSelectedTeam"
              placeholder="Select Team"
              filterable
              class="team-select"
              @change="handleTeamChange"
            >
              <el-option
                v-if="user.user_type !== 'leadership'"
                :label="`All Teams (${allTeamsCount})`"
                :value="''"
              />
              <el-option
                v-for="team in teamOptions"
                :key="team.key"
                :label="team.name"
                :value="team.key"
              />
            </el-select>

            <el-select
              v-model="localSelectedXAxis"
              placeholder="X-Axis"
              filterable
              class="axis-select"
              @change="handleXAxisChange"
            >
              <el-option
                v-for="opt in axisOptions"
                :key="'x-' + opt.key"
                :label="opt.label"
                :value="opt.key"
              />
            </el-select>

            <el-tooltip content="Swap axes" placement="top">
              <button class="btn-swap" @click="swapAxes">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
                  <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
                </svg>
              </button>
            </el-tooltip>

            <el-select
              v-model="localSelectedYAxis"
              placeholder="Y-Axis"
              filterable
              class="axis-select"
              @change="handleYAxisChange"
            >
              <el-option
                v-for="opt in axisOptions"
                :key="'y-' + opt.key"
                :label="opt.label"
                :value="opt.key"
              />
            </el-select>
          </div>

          <div class="header-right">
            <el-select
              v-model="localColorBy"
              class="color-by-select"
              @change="handleColorByChange"
            >
              <el-option label="Color by Talent Type" value="talent_type" />
              <el-option label="Color by Team" value="team" />
              <el-option label="No Coloring" value="none" />
            </el-select>

            <el-tooltip content="Export CSV" placement="top">
              <svg
                @click="exportCSV"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                class="icon-export"
              >
                <path
                  fill="currentColor"
                  d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"
                />
              </svg>
            </el-tooltip>
          </div>
        </div>

        <!-- Sub-teams toggle hidden — always on by default, can restore later -->
        <!-- <div class="header-row header-row-filters">
          <el-tooltip content="When enabled, includes employees from all child teams" placement="top">
            <div class="toggle-wrapper">
              <el-switch
                v-model="includeSubteams"
                active-color="#52c1c7"
                inactive-color="#dcdfe6"
              />
              <span class="toggle-label">Include Sub-teams</span>
            </div>
          </el-tooltip>
        </div> -->
      </div>

      <!-- Dimension Info Bar -->
      <div class="dimension-info-bar">
        <div class="dimension-info-item">
          <div class="dimension-info-header">
            <span class="dimension-info-axis">X-Axis</span>
            <span class="dimension-info-label">{{ xAxisLabel }}</span>
            <span v-if="xAxisConfig" class="dimension-info-polarity" :class="xAxisConfig.highIsGood ? 'polarity-positive' : 'polarity-negative'">
              {{ xAxisConfig.highIsGood ? 'Higher is better' : 'Lower is better' }}
            </span>
          </div>
          <div v-if="xAxisConfig && xAxisConfig.description" class="dimension-info-desc">{{ xAxisConfig.description }}</div>
          <div v-if="xAxisConfig && xAxisConfig.dataNote" class="dimension-info-data">{{ xAxisConfig.dataNote }}</div>
        </div>
        <div class="dimension-info-divider"></div>
        <div class="dimension-info-item">
          <div class="dimension-info-header">
            <span class="dimension-info-axis">Y-Axis</span>
            <span class="dimension-info-label">{{ yAxisLabel }}</span>
            <span v-if="yAxisConfig" class="dimension-info-polarity" :class="yAxisConfig.highIsGood ? 'polarity-positive' : 'polarity-negative'">
              {{ yAxisConfig.highIsGood ? 'Higher is better' : 'Lower is better' }}
            </span>
          </div>
          <div v-if="yAxisConfig && yAxisConfig.description" class="dimension-info-desc">{{ yAxisConfig.description }}</div>
          <div v-if="yAxisConfig && yAxisConfig.dataNote" class="dimension-info-data">{{ yAxisConfig.dataNote }}</div>
        </div>
      </div>

      <!-- Chart Area -->
      <div class="chart-wrapper">
        <div v-if="loading" class="matrix-loading-overlay">
          <div class="matrix-loader">
            <div class="matrix-cell cell-tl"></div>
            <div class="matrix-cell cell-tr"></div>
            <div class="matrix-cell cell-bl"></div>
            <div class="matrix-cell cell-br"></div>
          </div>
        </div>
        <canvas ref="talentMatrixChart"></canvas>

        <!-- Custom Tooltip -->
        <div
          class="chart-tooltip"
          :class="{ visible: tooltipVisible }"
          :style="tooltipStyle"
        >
          <template v-if="tooltipData">
            <div class="tooltip-header">
              <div class="tooltip-avatar" :class="[ blurMode ? 'blurelement' : '' ]">
                <img v-if="tooltipData.pic" :src="tooltipData.pic" />
                <span v-else>{{ getInitials(tooltipData.name) }}</span>
              </div>
              <div>
                <div class="tooltip-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ tooltipData.name }}</div>
                <div class="tooltip-team" :class="[ blurMode ? 'blurelement' : '' ]">{{ tooltipData.team_name }} <span v-if="tooltipData.talent_type">&middot; {{ tooltipData.talent_type }}</span></div>
              </div>
            </div>
            <div class="tooltip-metrics">
              <div class="tooltip-metric">
                <span class="tooltip-metric-label">{{ xAxisLabel }}</span>
                <span class="tooltip-metric-val">{{ tooltipData.xVal }}</span>
              </div>
              <div class="tooltip-metric">
                <span class="tooltip-metric-label">{{ yAxisLabel }}</span>
                <span class="tooltip-metric-val">{{ tooltipData.yVal }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Legend -->
      <div class="chart-legend" v-if="legendItems.length > 0">
        <div
          v-for="item in legendItems"
          :key="item.label"
          class="legend-item"
        >
          <span class="legend-dot" :style="{ background: item.color }"></span>
          {{ item.label }}
        </div>
      </div>

      <!-- Quadrant Summary Pills -->
      <div class="quadrant-summary">
        <div :class="['q-pill', quadrantPillClass('tr')]">
          <span class="q-count">{{ quadrantCounts.tr }}</span>
          {{ quadrantLabels.tr }}
        </div>
        <div :class="['q-pill', quadrantPillClass('tl')]">
          <span class="q-count">{{ quadrantCounts.tl }}</span>
          {{ quadrantLabels.tl }}
        </div>
        <div :class="['q-pill', quadrantPillClass('br')]">
          <span class="q-count">{{ quadrantCounts.br }}</span>
          {{ quadrantLabels.br }}
        </div>
        <div :class="['q-pill', quadrantPillClass('bl')]">
          <span class="q-count">{{ quadrantCounts.bl }}</span>
          {{ quadrantLabels.bl }}
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-header-row">
        <span class="table-title">Employee Details</span>
        <span class="table-count">{{ tableData.length }} employees</span>
      </div>

      <el-table
        :data="tableData"
        class="talent-table"
        :row-class-name="'talent-table-row'"
        :header-cell-style="{ background: '#fafafa', borderBottom: '1px solid #e0e0e0', color: '#909399', fontSize: '12px', fontWeight: '600', padding: '8px 0', whiteSpace: 'nowrap' }"
        :cell-style="{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }"
        @row-click="handleRowClick"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="name" label="Employee" sortable="custom" width="220" fixed>
          <template slot-scope="scope">
            <div class="emp-cell">
              <div class="emp-avatar" :class="[ blurMode ? 'blurelement' : '' ]">
                <img v-if="scope.row.pic || scope.row.profilepic_small" :src="scope.row.pic || scope.row.profilepic_small" />
                <span v-else>{{ getInitials(scope.row.name) }}</span>
              </div>
              <div class="emp-info">
                <div class="emp-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.name }}</div>
                <div class="emp-team-small" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.team_name }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column :prop="'xVal'" :label="xAxisLabel" sortable="custom" width="130" align="center">
          <template slot-scope="scope">
            <span class="metric-pill" :style="getMetricPillStyle(scope.row.xVal, selectedXAxis)">{{ scope.row.xVal }}{{ isPercentMetric(selectedXAxis) ? '%' : '' }}</span>
          </template>
        </el-table-column>
        <el-table-column :prop="'yVal'" :label="yAxisLabel" sortable="custom" width="130" align="center">
          <template slot-scope="scope">
            <span class="metric-pill" :style="getMetricPillStyle(scope.row.yVal, selectedYAxis)">{{ scope.row.yVal }}{{ isPercentMetric(selectedYAxis) ? '%' : '' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="network_type" label="Network" sortable="custom" width="150" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.network_type" class="network-badge" :style="getNetworkBadgeStyle(scope.row.network_type)">
              <span class="badge-dot" :style="{ background: getNetworkColor(scope.row.network_type) }"></span>
              {{ scope.row.network_type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="talent_type" label="Talent Type" sortable="custom" width="160" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.talent_type" class="talent-badge" :style="getTalentBadgeStyle(scope.row.talent_type)">
              <span class="badge-dot" :style="{ background: getTalentColor(scope.row.talent_type) }"></span>
              {{ scope.row.talent_type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="quadrant" label="Quadrant" width="250" align="center">
          <template slot-scope="scope">
            <span class="quadrant-tag" :class="scope.row.quadrantClass">{{ scope.row.quadrant }}</span>
          </template>
        </el-table-column>
      </el-table>

      <!-- Detail Drawer -->
      <el-drawer
        :visible.sync="drawerVisible"
        direction="rtl"
        size="420px"
        :show-close="true"
        :with-header="false"
      >
        <div v-if="drawerEmployee" class="drawer-content">
          <!-- Profile Header -->
          <div class="drawer-profile">
            <div class="drawer-profile-top">
              <div class="drawer-avatar-ring" :class="[ blurMode ? 'blurelement' : '' ]">
                <div class="drawer-avatar">
                  <img v-if="drawerEmployee.pic || drawerEmployee.profilepic_small" :src="drawerEmployee.pic || drawerEmployee.profilepic_small" />
                  <span v-else>{{ getInitials(drawerEmployee.name) }}</span>
                </div>
              </div>
              <div class="drawer-profile-info">
                <div class="drawer-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ drawerEmployee.name }}</div>
                <div class="drawer-meta" :class="[ blurMode ? 'blurelement' : '' ]">{{ drawerEmployee.team_name }}</div>
                <div class="drawer-tags">
                  <span v-if="drawerEmployee.talent_type" class="talent-badge" :style="getTalentBadgeStyle(drawerEmployee.talent_type)">
                    <span class="badge-dot" :style="{ background: getTalentColor(drawerEmployee.talent_type) }"></span>
                    {{ drawerEmployee.talent_type }}
                  </span>
                  <span class="quadrant-tag" :class="drawerEmployee.quadrantClass">
                    {{ drawerEmployee.quadrant }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Engagement & Well-being -->
          <div class="drawer-section">
            <div class="drawer-section-title">Engagement & Well-being</div>
            <div class="metric-row" v-for="m in [
              { label: 'Happiness Score', key: 'healthbar', max: 100, suffix: '' },
              { label: 'Engagement Score', key: 'computed_engagement', max: 100, suffix: '' },
              { label: 'Flight Risk', key: 'computed_flight_risk', max: 100, suffix: '%', invert: true },
            ]" :key="'ew-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max, m.invert) }"></div>
                </div>
                <span class="metric-value">{{ getMetricValue(drawerEmployee, m.key) }}{{ m.suffix }}</span>
              </div>
            </div>
          </div>

          <!-- Performance & Growth -->
          <div class="drawer-section">
            <div class="drawer-section-title">Performance & Growth</div>
            <div class="metric-row" v-for="m in [
              { label: 'Impact Rating', key: 'impact_rating', max: 150, suffix: '' },
              { label: 'Performance Score', key: 'computed_performance', max: 100, suffix: '%' },
              { label: 'Focus Score', key: 'computed_focus', max: 100, suffix: '' },
              { label: 'Goal Alignment', key: 'computed_goal_alignment', max: 100, suffix: '%' },
            ]" :key="'pg-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max) }"></div>
                </div>
                <span class="metric-value">{{ getMetricValue(drawerEmployee, m.key) }}{{ m.suffix }}</span>
              </div>
            </div>
          </div>

          <!-- Recognition & Trust -->
          <div class="drawer-section">
            <div class="drawer-section-title">Recognition & Trust</div>
            <div class="metric-row" v-for="m in [
              { label: 'Trust Score', key: 'computed_trust', max: 100, suffix: '' },
              { label: 'Peer Trust Count', key: 'computed_peer_trust', max: 15, suffix: '' },
              { label: 'Giving Recognition', key: 'computed_giving_recognition', max: 100, suffix: '' },
              { label: 'Receiving Recognition', key: 'computed_receiving_recognition', max: 100, suffix: '' },
              { label: 'Medals', key: 'medalCount', max: 30, suffix: '' },
            ]" :key="'rt-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max) }"></div>
                </div>
                <span class="metric-value">{{ getMetricValue(drawerEmployee, m.key) }}{{ m.suffix }}</span>
              </div>
            </div>
          </div>

          <!-- Power Skills (EQ) -->
          <div class="drawer-section">
            <div class="drawer-section-title">Power Skills (EQ)</div>
            <div v-for="ps in powerSkillKeys" :key="ps.key" class="metric-row">
              <span class="metric-label">{{ ps.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, ps.key, 10) + '%', background: getScoreColor(getMetricValue(drawerEmployee, ps.key), 10) }"></div>
                </div>
                <span class="metric-value">{{ getMetricValue(drawerEmployee, ps.key) }}</span>
              </div>
            </div>
          </div>
        </div>
      </el-drawer>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import { AXIS_OPTIONS } from '@/store/talent-matrix'
import { getTeamByCompany } from '@/store/core/cacheService'

// Unique colors per talent type based on grid position
// Grid: X = Organizational Contribution (low→high), Y = Individual Performance (low→high)
// Colors progress from red (bottom-left) through amber/teal to green (top-right)
const TALENT_COLORS = {
  // Row 4 (highest individual performance) — greens
  'Superstar':           '#1B5E20',  // top-right: best overall
  'High Performer':      '#2E7D32',  // high perf, high-mid contrib
  'Activated':           '#558B2F',  // high perf, low-mid contrib
  'Untapped Potential':  '#9E9D24',  // high perf, low contrib — potential not leveraged

  // Row 3 — teal/green spectrum
  'Distinguished Talent':'#00695C',  // high-mid perf, highest contrib
  'Respected Expert':    '#00897B',  // high-mid perf, high contrib
  'Key Player':          '#26A69A',  // high-mid perf, low-mid contrib
  'Valued Contributor':  '#4DB6AC',  // high-mid perf, low contrib

  // Row 2 — blue/purple spectrum
  'Solid Performer':     '#1565C0',  // mid perf, highest contrib
  'Core Player':         '#1976D2',  // mid perf, high contrib
  'Engaged Contributor': '#42A5F5',  // mid perf, low-mid contrib
  'Community Pillar':    '#5C6BC0',  // mid perf, low contrib — strong socially

  // Row 1 (lowest individual performance) — amber/orange/red
  'High Potential':      '#F57F17',  // low perf, highest contrib — promising
  'Emerging Talent':     '#FF8F00',  // low perf, high contrib — developing
  'Rising Star':         '#FF6D00',  // low perf, low-mid contrib — early stage
  'Up-and-Coming':       '#E65100',  // low perf, low-mid contrib

  // Bottom row edge
  'Unsung Talent':       '#5D4037',  // low perf, highest contrib — deep brown, underrecognized
  'Crowd Favorite':      '#AB47BC',  // low perf, high contrib — purple, liked but low output
  'Contributor':         '#78909C',  // low perf, low-mid contrib — blue-gray, steady but basic
  'Latent Potential':    '#C62828',  // lowest — needs intervention

  // Legacy names
  'Up-and-coming':       '#E65100',
  'High Contributor':    '#26A69A',
  'Developing':          '#FF8F00',
  'Hidden Talent':       '#5D4037',
  'Supported':           '#AB47BC',
  'Underutilized':       '#9E9D24',
  'Marginalized':        '#C62828',
}

const TEAM_COLORS = ['#6C30CC', '#3AC47D', '#F7B924', '#2196F3', '#FF6B6B', '#26A69A', '#FF9800', '#9C27B0', '#607D8B', '#795548']

export default {
  name: 'TalentMatrix',

  data() {
    return {
      chart: null,
      teamOptions: [],
      allTeamsCount: 0,
      drawerVisible: false,
      drawerEmployee: null,
      searchKeyword: '',
      includeSubteams: true,
      tooltipVisible: false,
      tooltipData: null,
      tooltipStyle: {},
      localSelectedTeam: '',
      localSelectedXAxis: 'impact_rating',
      localSelectedYAxis: 'healthbar',
      localColorBy: 'talent_type',
      sortProp: null,
      sortOrder: null,
    }
  },

  computed: {
    ...mapGetters('authen', { user: 'user' }),
    ...mapGetters('talentMatrix', ['loading']),
    blurMode() {
      return this.$store.getters['demomode/blurMode']
    },

    selectedXAxis() { return this.localSelectedXAxis },
    selectedYAxis() { return this.localSelectedYAxis },
    selectedTeam() { return this.localSelectedTeam },
    colorBy() { return this.localColorBy },

    axisOptions() { return AXIS_OPTIONS },

    xAxisConfig() { return AXIS_OPTIONS.find(o => o.key === this.selectedXAxis) },
    yAxisConfig() { return AXIS_OPTIONS.find(o => o.key === this.selectedYAxis) },
    xAxisLabel() { return this.xAxisConfig ? this.xAxisConfig.label : '' },
    yAxisLabel() { return this.yAxisConfig ? this.yAxisConfig.label : '' },
    xAxisRange() { return this.computeAxisRange(this.selectedXAxis) },
    yAxisRange() { return this.computeAxisRange(this.selectedYAxis) },

    allEmployees() { return this.$store.state.people ? (this.$store.state.people.starperformall || []) : [] },

    childrenByParent() {
      var index = {}
      this.teamOptions.forEach(function (team) {
        if (team.parent) {
          if (!index[team.parent]) index[team.parent] = []
          index[team.parent].push(team.key)
        }
      })
      return index
    },

    selectedTeamDescendants() {
      if (!this.localSelectedTeam) return null
      var descendants = new Set([this.localSelectedTeam])
      if (this.includeSubteams) {
        var queue = [this.localSelectedTeam]
        var childrenIndex = this.childrenByParent
        while (queue.length > 0) {
          var parentId = queue.shift()
          var children = childrenIndex[parentId] || []
          for (var i = 0; i < children.length; i++) {
            if (!descendants.has(children[i])) {
              descendants.add(children[i])
              queue.push(children[i])
            }
          }
        }
      }
      return descendants
    },

    filteredEmployees() {
      var emps = this.allEmployees
      var teamSet = this.selectedTeamDescendants
      if (teamSet) {
        emps = emps.filter(function (e) { return teamSet.has(e.team) })
      }
      if (this.searchKeyword) {
        var q = this.searchKeyword.toLowerCase()
        emps = emps.filter(function (e) {
          return (e.fullname || '').toLowerCase().indexOf(q) !== -1 ||
            (e.team_name || '').toLowerCase().indexOf(q) !== -1 ||
            (e.talent || '').toLowerCase().indexOf(q) !== -1
        })
      }
      return emps
    },

    tableData() {
      let data = this.filteredEmployees.map(emp => ({
        ...emp,
        name: this.getDisplayName(emp),
        team_name: emp.team_name || '',
        talent_type: emp.talent || '',
        network_type: emp.network_type || '',
        xVal: this.getMetricValue(emp, this.selectedXAxis),
        yVal: this.getMetricValue(emp, this.selectedYAxis),
        quadrant: this.getQuadrantLabel(emp),
        quadrantClass: this.getQuadrantClass(emp),
      }))

      if (this.sortProp && this.sortOrder) {
        var prop = this.sortProp
        var asc = this.sortOrder === 'ascending' ? 1 : -1
        data.sort(function (a, b) {
          var va = a[prop]
          var vb = b[prop]
          // Coerce to numbers for numeric columns (xVal, yVal, or any parseable value)
          var na = parseFloat(va)
          var nb = parseFloat(vb)
          if (!isNaN(na) && !isNaN(nb)) return (na - nb) * asc
          return String(va || '').localeCompare(String(vb || '')) * asc
        })
      }

      return data
    },

    quadrantCounts() {
      const counts = { tr: 0, tl: 0, br: 0, bl: 0 }
      this.filteredEmployees.forEach(emp => {
        const q = this.getQuadrantKey(emp)
        counts[q]++
      })
      return counts
    },

    quadrantLabels() {
      var x = this.selectedXAxis
      var y = this.selectedYAxis
      var LABELS = {
        'impact_rating-healthbar': {
          tl: 'Low Impact, Happy',
          tr: 'High Impact, Happy',
          bl: 'Low Impact, Unhappy',
          br: 'High Impact, Unhappy',
        },
        'impact_rating-computed_flight_risk': {
          tl: 'Underperforming at Risk',
          tr: 'Stars at Risk',
          bl: 'Steady Contributors',
          br: 'Stable Stars',
        },
        'impact_rating-computed_engagement': {
          tl: 'Low Impact, Engaged',
          tr: 'High Impact, Engaged',
          bl: 'Low Impact, Disengaged',
          br: 'High Impact, Disengaged',
        },
        'impact_rating-computed_trust': {
          tl: 'Low Impact, Trusted',
          tr: 'High Impact, Trusted',
          bl: 'Low Impact, Low Trust',
          br: 'High Impact, Low Trust',
        },
        'impact_rating-computed_performance': {
          tl: 'Low Impact, High Review',
          tr: 'High Impact, High Review',
          bl: 'Low Impact, Low Review',
          br: 'High Impact, Low Review',
        },
        'computed_focus-computed_goal_alignment': {
          tl: 'Low Focus, Aligned',
          tr: 'Focused & Aligned',
          bl: 'Low Focus, Misaligned',
          br: 'Focused, Misaligned',
        },
        'computed_giving_recognition-computed_receiving_recognition': {
          tl: 'Low Giver, Recognized',
          tr: 'Active Giver, Recognized',
          bl: 'Low Giver, Unrecognized',
          br: 'Active Giver, Unrecognized',
        },
      }
      var specific = LABELS[x + '-' + y]
      if (specific) return specific
      // Generate short labels by removing common suffixes
      var xShort = this.xAxisLabel.replace(/ Score$/, '').replace(/ Rating$/, '')
      var yShort = this.yAxisLabel.replace(/ Score$/, '').replace(/ Rating$/, '')
      return {
        tl: 'Low ' + xShort + ', High ' + yShort,
        tr: 'High ' + xShort + ', High ' + yShort,
        bl: 'Low ' + xShort + ', Low ' + yShort,
        br: 'High ' + xShort + ', Low ' + yShort,
      }
    },

    drawerTitle() {
      if (this.drawerEmployee) return this.drawerEmployee.name
      return 'Employee Detail'
    },

    legendItems() {
      if (this.colorBy === 'none') return []

      var items = []
      var seen = {}

      if (this.colorBy === 'talent_type') {
        this.filteredEmployees.forEach(function (emp) {
          var t = emp.talent || 'Unknown'
          if (!seen[t]) {
            seen[t] = true
            items.push({ label: t, color: TALENT_COLORS[t] || '#999' })
          }
        })
      } else if (this.colorBy === 'team') {
        var teamColorMap = {}
        var colorIdx = 0
        this.filteredEmployees.forEach(function (emp) {
          var t = emp.team_name || emp.team || 'Unknown'
          if (!seen[t]) {
            seen[t] = true
            if (!teamColorMap[t]) {
              teamColorMap[t] = TEAM_COLORS[colorIdx % TEAM_COLORS.length]
              colorIdx++
            }
            items.push({ label: t, color: teamColorMap[t] })
          }
        })
      }

      return items
    },

    powerSkillKeys() {
      return AXIS_OPTIONS.filter(function (o) { return o.key.startsWith('ps_') }).map(function (o) {
        return { key: o.key, label: o.label.replace(' (EQ)', '') }
      })
    },
  },

  watch: {
    filteredEmployees: function () { this.updateChart() },
    selectedXAxis: function () { this.updateChart() },
    selectedYAxis: function () { this.updateChart() },
    colorBy: function () { this.updateChart() },
    includeSubteams: function () { this.updateChart() },
  },

  async mounted() {
    await this.loadData()
    this.syncFromStore()
    // Chart creation handled by watchers reacting to data load
    // Only create if watchers haven't already
    if (!this.chart) {
      this.createChart()
    }
  },

  beforeDestroy() {
    if (this.chart) this.chart.destroy()
  },

  methods: {
    syncFromStore() {
      var state = this.$store.state.talentMatrix
      if (state) {
        this.localSelectedTeam = state.selectedTeam || ''
        this.localSelectedXAxis = state.selectedXAxis || 'impact_rating'
        this.localSelectedYAxis = state.selectedYAxis || 'healthbar'
        this.localColorBy = state.colorBy || 'talent_type'
      }
    },

    async loadData() {
      var user = this.user
      await this.$store.dispatch('talentMatrix/loadMatrixData', user)
      // Load composite scores (flight risk, engagement, trust, focus, etc.)
      await this.$store.dispatch('talentMatrix/loadCompositeScores', user)
      if (user && user.company) {
        var teams = await getTeamByCompany(user.company)
        this.teamOptions = []
        if (teams) {
          var self = this
          Object.keys(teams).forEach(function (key) {
            if (teams[key] && teams[key].name) {
              self.teamOptions.push({ key: key, name: teams[key].name, parent: teams[key].parent || null })
            }
          })
        }
        this.allTeamsCount = this.teamOptions.length
      }
    },

    getMetricValue(emp, key) {
      if (key.startsWith('computed_')) {
        var compositeScores = this.$store.state.talentMatrix.compositeScores
        if (compositeScores && emp.email && compositeScores[emp.email]) {
          return compositeScores[emp.email][key] || 0
        }
        return 0
      }
      if (key.startsWith('ps_')) {
        if (emp.power_tag && emp.power_tag[key]) {
          var tag = emp.power_tag[key]
          return tag.point_total > 0 ? Math.round((tag.point / tag.point_total) * 100) / 10 : 0
        }
        return 0
      }
      var raw = emp[key]
      if (raw == null) return 0
      var num = parseFloat(raw)
      return isNaN(num) ? 0 : num
    },

    computeAxisRange(metricKey) {
      var config = AXIS_OPTIONS.find(function (o) { return o.key === metricKey })
      if (!config) return { min: -2, max: 102 }
      if (!config.dynamic) {
        // Add edge padding so dots at min/max don't get clipped
        // For 0-100 scales, use 0 to ~103 so axis labels stay clean
        var span = config.max - config.min
        var edgePad = Math.max(span * 0.03, 1)
        return { min: config.min, max: config.max + edgePad }
      }
      var values = []
      var self = this
      this.filteredEmployees.forEach(function (e) {
        var v = self.getMetricValue(e, metricKey)
        values.push(v)
      })
      if (values.length === 0) {
        var s = config.max - config.min
        var ep = s * 0.03 || 1
        return { min: config.min - ep, max: config.max + ep }
      }
      var dataMin = Math.min.apply(null, values)
      var dataMax = Math.max.apply(null, values)
      var padding = (dataMax - dataMin) * 0.08 || 2
      return {
        min: Math.floor(dataMin - padding),
        max: Math.ceil(dataMax + padding)
      }
    },

    getQuadrantKey(emp) {
      var xMid = (this.xAxisRange.min + this.xAxisRange.max) / 2
      var yMid = (this.yAxisRange.min + this.yAxisRange.max) / 2
      var xVal = this.getMetricValue(emp, this.selectedXAxis)
      var yVal = this.getMetricValue(emp, this.selectedYAxis)
      var highX = xVal >= xMid
      var highY = yVal >= yMid
      if (highX && highY) return 'tr'
      if (!highX && highY) return 'tl'
      if (highX && !highY) return 'br'
      return 'bl'
    },

    getQuadrantLabel(emp) {
      var key = this.getQuadrantKey(emp)
      return this.quadrantLabels[key] || ''
    },

    getQuadrantClass(emp) {
      var key = this.getQuadrantKey(emp)
      return this.quadrantTagClass(key)
    },

    quadrantSentimentFor(quadKey) {
      // Returns: 3=critical, 2=success, 1=warning, 0=danger
      // Critical = valuable person with a risk signal (most urgent to act on)
      var xCfg = this.xAxisConfig || {}
      var yCfg = this.yAxisConfig || {}
      var xHighGood = xCfg.highIsGood !== false
      var yHighGood = yCfg.highIsGood !== false
      var highX = quadKey === 'tr' || quadKey === 'br'
      var highY = quadKey === 'tl' || quadKey === 'tr'
      var xGood = highX ? xHighGood : !xHighGood
      var yGood = highY ? yHighGood : !yHighGood

      if (xGood && yGood) return 2     // both good → success
      if (!xGood && !yGood) return 0   // both bad → danger

      // Mixed: one good, one bad. Determine urgency.
      // Critical = the good dimension shows strength (highIsGood=true and value is high)
      //            AND the bad dimension is a negative/risk metric showing a problem.
      // Warning  = merely underperforming on a positive metric, no active risk signal.
      var hasRiskSignal = (!xGood && !xHighGood) || (!yGood && !yHighGood)
      var hasStrength = (xGood && xHighGood) || (yGood && yHighGood)
      if (hasRiskSignal && hasStrength) return 3  // valuable person at risk → critical
      return 1                                     // underperforming but no risk → warning
    },

    quadrantPillClass(quadKey) {
      var sent = this.quadrantSentimentFor(quadKey)
      return { 3: 'pill-critical', 2: 'pill-success', 1: 'pill-warning', 0: 'pill-danger' }[sent] || 'pill-neutral'
    },

    quadrantTagClass(quadKey) {
      var sent = this.quadrantSentimentFor(quadKey)
      return { 3: 'qt-critical', 2: 'qt-success', 1: 'qt-warning', 0: 'qt-danger' }[sent] || 'qt-neutral'
    },

    handleTeamChange(val) {
      this.$store.commit('talentMatrix/setSelectedTeam', val)
    },

    handleXAxisChange(val) {
      this.$store.commit('talentMatrix/setSelectedXAxis', val)
    },

    handleYAxisChange(val) {
      this.$store.commit('talentMatrix/setSelectedYAxis', val)
    },

    handleColorByChange(val) {
      this.$store.commit('talentMatrix/setColorBy', val)
    },

    swapAxes() {
      var x = this.localSelectedXAxis
      var y = this.localSelectedYAxis
      this.localSelectedXAxis = y
      this.localSelectedYAxis = x
      this.$store.commit('talentMatrix/setSelectedXAxis', y)
      this.$store.commit('talentMatrix/setSelectedYAxis', x)
    },

    isPercentMetric(key) {
      return key === 'computed_flight_risk' ||
        key === 'computed_performance' ||
        key === 'computed_goal_alignment'
    },

    getScoreColor(value, max, invert) {
      var pct = max > 0 ? value / max : 0
      if (invert) pct = 1 - pct
      if (pct >= 0.7) return '#2E7D32'
      if (pct >= 0.4) return '#F9A825'
      return '#CC3030'
    },

    getBarWidth(emp, key, max) {
      var val = this.getMetricValue(emp, key)
      if (!max) return 0
      return Math.min(100, Math.round((val / max) * 100))
    },

    getTalentColor(talent) {
      return TALENT_COLORS[talent] || '#999'
    },

    badgeStyle(color) {
      return { background: color + '0F', color: color }
    },

    getTalentBadgeStyle(talent) {
      return this.badgeStyle(this.getTalentColor(talent))
    },

    getNetworkColor(networkType) {
      var t = (networkType || '').toLowerCase()
      if (t.indexOf('super') >= 0) return '#1B5E20'
      if (t.indexOf('high') >= 0) return '#00897B'
      if (t.indexOf('medium') >= 0) return '#F9A825'
      return '#909399'
    },

    getNetworkBadgeStyle(networkType) {
      return this.badgeStyle(this.getNetworkColor(networkType))
    },

    getMetricPillStyle(value, axisKey) {
      var config = AXIS_OPTIONS.find(function (o) { return o.key === axisKey })
      if (!config) return {}
      // Impact Rating uses absolute thresholds (matching people.vue)
      if (axisKey === 'impact_rating') {
        var impactColor = value >= 70 ? '#2E7D32' : value >= 40 ? '#F9A825' : '#CC3030'
        return this.badgeStyle(impactColor)
      }
      return this.badgeStyle(this.getScoreColor(value, config.max || 100, !config.highIsGood))
    },

    getDisplayName(emp) {
      return emp.fullname || ((emp.firstname || '') + ' ' + (emp.lastname || '')).trim() || emp.email || ''
    },

    getInitials(name) {
      if (!name) return '?'
      var parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name[0].toUpperCase()
    },

    isDiscreteMetric(key) {
      return key === 'risk_level' || key === 'star'
    },

    getChartValue(emp, key) {
      var val = this.getMetricValue(emp, key)
      if (this.isDiscreteMetric(key)) {
        // Add small jitter for discrete metrics so overlapping dots separate
        val += (Math.random() - 0.5) * 0.3
      }
      return val
    },

    buildDatasets() {
      var self = this
      var employees = this.filteredEmployees
      var xKey = this.selectedXAxis
      var yKey = this.selectedYAxis

      if (this.colorBy === 'none') {
        return [{
          label: 'Employees',
          data: employees.map(function (emp) {
            return {
              x: self.getChartValue(emp, xKey),
              y: self.getChartValue(emp, yKey),
              emp: emp,
            }
          }),
          backgroundColor: 'rgba(108, 48, 204, 0.6)',
          borderColor: 'rgba(108, 48, 204, 0.8)',
          pointRadius: 7,
          pointHoverRadius: 10,
        }]
      }

      var groups = {}
      var groupColors = {}
      var teamColorIdx = 0

      employees.forEach(function (emp) {
        var groupKey
        if (self.colorBy === 'talent_type') {
          groupKey = emp.talent || 'Unknown'
          groupColors[groupKey] = TALENT_COLORS[groupKey] || '#999'
        } else {
          groupKey = emp.team_name || emp.team || 'Unknown'
          if (!groupColors[groupKey]) {
            groupColors[groupKey] = TEAM_COLORS[teamColorIdx % TEAM_COLORS.length]
            teamColorIdx++
          }
        }
        if (!groups[groupKey]) groups[groupKey] = []
        groups[groupKey].push({
          x: self.getChartValue(emp, xKey),
          y: self.getChartValue(emp, yKey),
          emp: emp,
        })
      })

      return Object.keys(groups).map(function (key) {
        var color = groupColors[key]
        return {
          label: key,
          data: groups[key],
          backgroundColor: color + '99',
          borderColor: color,
          pointRadius: 7,
          pointHoverRadius: 10,
        }
      })
    },

    createChart() {
      var canvas = this.$refs.talentMatrixChart
      if (!canvas) return
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }

      var self = this
      var xCfgBase = this.xAxisConfig || { min: 0, max: 100, label: '' }
      var yCfgBase = this.yAxisConfig || { min: 0, max: 100, label: '' }
      var xCfg = { min: this.xAxisRange.min, max: this.xAxisRange.max, label: xCfgBase.label }
      var yCfg = { min: this.yAxisRange.min, max: this.yAxisRange.max, label: yCfgBase.label }

      var quadrantLabels = this.quadrantLabels
      var xMid = (xCfg.min + xCfg.max) / 2
      var yMid = (yCfg.min + yCfg.max) / 2

      // Compute quadrant sentiments using the shared method
      var COLORS_BG = { 3: 'rgba(183, 28, 28, 0.08)', 2: 'rgba(58, 196, 125, 0.08)', 1: 'rgba(247, 185, 36, 0.06)', 0: 'rgba(204, 48, 48, 0.06)' }
      var COLORS_LABEL = { 3: 'rgba(183, 28, 28, 0.5)', 2: 'rgba(26, 138, 74, 0.5)', 1: 'rgba(184, 134, 11, 0.45)', 0: 'rgba(204, 48, 48, 0.45)' }

      var tlSent = self.quadrantSentimentFor('tl')
      var trSent = self.quadrantSentimentFor('tr')
      var blSent = self.quadrantSentimentFor('bl')
      var brSent = self.quadrantSentimentFor('br')

      // Chart.js plugin to draw quadrant backgrounds + labels aligned to chart area
      var quadrantPlugin = {
        id: 'quadrantBackgrounds',
        beforeDraw: function (chart) {
          var ctx = chart.ctx
          var chartArea = chart.chartArea
          if (!chartArea) return
          var xScale = chart.scales.x
          var yScale = chart.scales.y
          var midXPx = xScale.getPixelForValue(xMid)
          var midYPx = yScale.getPixelForValue(yMid)

          // Clamp to chart area
          midXPx = Math.max(chartArea.left, Math.min(chartArea.right, midXPx))
          midYPx = Math.max(chartArea.top, Math.min(chartArea.bottom, midYPx))

          // Draw quadrant backgrounds with polarity-aware colors
          var quadrants = [
            { x1: chartArea.left, y1: chartArea.top, x2: midXPx, y2: midYPx, color: COLORS_BG[tlSent] },
            { x1: midXPx, y1: chartArea.top, x2: chartArea.right, y2: midYPx, color: COLORS_BG[trSent] },
            { x1: chartArea.left, y1: midYPx, x2: midXPx, y2: chartArea.bottom, color: COLORS_BG[blSent] },
            { x1: midXPx, y1: midYPx, x2: chartArea.right, y2: chartArea.bottom, color: COLORS_BG[brSent] },
          ]
          quadrants.forEach(function (q) {
            ctx.fillStyle = q.color
            ctx.fillRect(q.x1, q.y1, q.x2 - q.x1, q.y2 - q.y1)
          })

          // Draw midlines (dashed)
          ctx.save()
          ctx.setLineDash([4, 4])
          ctx.lineWidth = 1
          ctx.strokeStyle = 'rgba(108, 48, 204, 0.18)'
          ctx.beginPath()
          ctx.moveTo(midXPx, chartArea.top)
          ctx.lineTo(midXPx, chartArea.bottom)
          ctx.stroke()
          ctx.beginPath()
          ctx.moveTo(chartArea.left, midYPx)
          ctx.lineTo(chartArea.right, midYPx)
          ctx.stroke()
          ctx.restore()

          // Draw quadrant labels
          ctx.save()
          ctx.font = '600 10px Lato, sans-serif'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          var labels = [
            { text: quadrantLabels.tl, cx: (chartArea.left + midXPx) / 2, cy: (chartArea.top + midYPx) / 2, color: COLORS_LABEL[tlSent] },
            { text: quadrantLabels.tr, cx: (midXPx + chartArea.right) / 2, cy: (chartArea.top + midYPx) / 2, color: COLORS_LABEL[trSent] },
            { text: quadrantLabels.bl, cx: (chartArea.left + midXPx) / 2, cy: (midYPx + chartArea.bottom) / 2, color: COLORS_LABEL[blSent] },
            { text: quadrantLabels.br, cx: (midXPx + chartArea.right) / 2, cy: (midYPx + chartArea.bottom) / 2, color: COLORS_LABEL[brSent] },
          ]
          labels.forEach(function (lbl) {
            ctx.fillStyle = lbl.color
            var lines = lbl.text.split(', ')
            if (lines.length === 1) lines = lbl.text.split(' ')
            // Render as single line, uppercase
            ctx.fillText(lbl.text.toUpperCase(), lbl.cx, lbl.cy)
          })
          ctx.restore()
        }
      }

      this.chart = new Chart(canvas, {
        type: 'scatter',
        data: {
          datasets: this.buildDatasets(),
        },
        plugins: [quadrantPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          clip: false,
          animation: { duration: 300 },
          layout: {
            padding: { top: 10, right: 10, bottom: 5, left: 5 }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: false,
              external: function (context) {
                var tooltipModel = context.tooltip
                if (tooltipModel.opacity === 0) {
                  self.tooltipVisible = false
                  return
                }
                if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
                  var dp = tooltipModel.dataPoints[0]
                  var raw = dp.raw
                  if (raw && raw.emp) {
                    self.tooltipData = {
                      name: self.getDisplayName(raw.emp),
                      team_name: raw.emp.team_name || '',
                      talent_type: raw.emp.talent || '',
                      pic: raw.emp.pic || raw.emp.profilepic_small || '',
                      xVal: self.getMetricValue(raw.emp, self.selectedXAxis),
                      yVal: self.getMetricValue(raw.emp, self.selectedYAxis),
                    }
                    // Position tooltip, flipping if near edges
                    var canvasRect = context.chart.canvas.getBoundingClientRect()
                    var tooltipW = 220
                    var tooltipH = 100
                    var caretX = tooltipModel.caretX
                    var caretY = tooltipModel.caretY
                    var leftPos = caretX + 14
                    var topPos = caretY - 14
                    // Flip left if tooltip would overflow right edge
                    if (caretX + tooltipW + 20 > canvasRect.width) {
                      leftPos = caretX - tooltipW - 14
                    }
                    // Flip up if tooltip would overflow bottom
                    if (caretY + tooltipH > canvasRect.height) {
                      topPos = caretY - tooltipH - 14
                    }
                    // Clamp top to 0
                    if (topPos < 0) topPos = 4
                    self.tooltipStyle = {
                      left: leftPos + 'px',
                      top: topPos + 'px',
                    }
                    self.tooltipVisible = true
                  }
                }
              },
            },
          },
          onClick: function (event, elements) {
            if (elements && elements.length > 0) {
              var el = elements[0]
              var datasetIndex = el.datasetIndex
              var index = el.index
              var raw = self.chart.data.datasets[datasetIndex].data[index]
              if (raw && raw.emp) {
                var emp = raw.emp
                self.openDrawer({
                  ...emp,
                  name: self.getDisplayName(emp),
                  team_name: emp.team_name || '',
                  talent_type: emp.talent || '',
                  xVal: self.getMetricValue(emp, self.selectedXAxis),
                  yVal: self.getMetricValue(emp, self.selectedYAxis),
                  quadrant: self.getQuadrantLabel(emp),
                  quadrantClass: self.getQuadrantClass(emp),
                })
              }
            }
          },
          scales: {
            x: {
              min: xCfg.min,
              max: xCfg.max,
              title: {
                display: true,
                text: xCfg.label,
                font: { size: 12, weight: '600', family: 'Lato' },
                color: '#5a5a7a',
                padding: { top: 8 },
              },
              grid: {
                color: 'rgba(232, 232, 240, 0.5)',
                lineWidth: 1,
              },
              ticks: {
                font: { size: 11, family: 'Lato' },
                color: '#8e8ea0',
                callback: function (value) {
                  if (value < 0) return ''
                  if (value > xCfgBase.max) return ''
                  return value
                },
              },
            },
            y: {
              min: yCfg.min,
              max: yCfg.max,
              title: {
                display: true,
                text: yCfg.label,
                font: { size: 12, weight: '600', family: 'Lato' },
                color: '#5a5a7a',
                padding: { bottom: 8 },
              },
              grid: {
                color: 'rgba(232, 232, 240, 0.5)',
                lineWidth: 1,
              },
              ticks: {
                font: { size: 11, family: 'Lato' },
                color: '#8e8ea0',
                callback: function (value) {
                  if (value < 0) return ''
                  if (value > yCfgBase.max) return ''
                  return value
                },
              },
            },
          },
        },
      })
    },

    updateChart() {
      if (this._updatePending) return
      this._updatePending = true
      this.$nextTick(function () {
        this._updatePending = false
        if (this._isDestroyed) return
        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }
        this.createChart()
      }.bind(this))
    },

    handleRowClick(row) {
      this.openDrawer(row)
    },

    openDrawer(emp) {
      this.drawerEmployee = emp
      this.drawerVisible = true
    },

    handleSortChange(sort) {
      this.sortProp = sort.prop
      this.sortOrder = sort.order
    },

    exportCSV() {
      var rows = [
        ['Employee', 'Email', this.xAxisLabel, this.yAxisLabel, 'Team', 'Network', 'Talent Type', 'Quadrant'],
      ]
      this.tableData.forEach(function (row) {
        rows.push([
          '"' + (row.name || '').replace(/"/g, '""') + '"',
          '"' + (row.email || '').replace(/"/g, '""') + '"',
          row.xVal,
          row.yVal,
          '"' + (row.team_name || '').replace(/"/g, '""') + '"',
          '"' + (row.network_type || '').replace(/"/g, '""') + '"',
          '"' + (row.talent_type || '').replace(/"/g, '""') + '"',
          '"' + (row.quadrant || '').replace(/"/g, '""') + '"',
        ])
      })

      var xShort = this.xAxisLabel.replace(/\s+/g, '-').toLowerCase()
      var yShort = this.yAxisLabel.replace(/\s+/g, '-').toLowerCase()
      var selectedTeam = this.selectedTeam
      var team = selectedTeam ? this.teamOptions.find(function (t) { return t.key === selectedTeam }) : null
      var teamSlug = team && team.name ? team.name.replace(/\s+/g, '-').toLowerCase() : 'all-teams'
      var filename = 'talent-matrix_' + teamSlug + '_' + xShort + '_vs_' + yShort + '.csv'

      var csv = rows.map(function (r) { return r.join(',') }).join('\n')
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      var link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    },
  },
}
</script>

<style scoped>
/* Header Controls - matches focus-areas.vue / goals.vue pattern */
.header-controls {
  padding: 24px 0 16px 0;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-row-primary {
  justify-content: space-between;
  margin-bottom: 12px;
}

.header-row-filters {
  gap: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left .team-select {
  width: 200px;
}

.axis-select {
  width: 180px;
}

.header-right .color-by-select {
  width: 180px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 0;
  white-space: nowrap;
}

.toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  user-select: none;
}

.btn-swap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(108, 48, 204, 0.06);
  border: 1.5px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6C30CC;
}

.btn-swap:hover {
  background: #6C30CC;
  color: white;
  transform: rotate(90deg);
}

.icon-export {
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;
}
.icon-export:hover {
  color: #6C30CC;
}

/* Dimension Info Bar */
.dimension-info-bar {
  display: flex;
  gap: 0;
  background: #f8f9fc;
  border: 1px solid #e8e8f0;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 16px;
  overflow: hidden;
}
.dimension-info-item {
  flex: 1;
  padding: 14px 18px;
}
.dimension-info-divider {
  width: 1px;
  background: #e8e8f0;
  flex-shrink: 0;
}
.dimension-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.dimension-info-axis {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: #6C30CC;
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}
.dimension-info-label {
  font-size: 13px;
  font-weight: 700;
  color: #2e2e2e;
}
.dimension-info-polarity {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  margin-left: auto;
}
.polarity-positive {
  background: rgba(58, 196, 125, 0.1);
  color: #1a8a4a;
}
.polarity-negative {
  background: rgba(204, 48, 48, 0.08);
  color: #CC3030;
}
.dimension-info-desc {
  font-size: 12px;
  color: #4a4a5a;
  line-height: 1.4;
  margin-bottom: 4px;
}
.dimension-info-data {
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
  font-style: italic;
}

/* Chart Wrapper */
.chart-wrapper {
  position: relative;
  height: 500px;
  margin-bottom: 8px;
}

/* Matrix Loading Indicator */
.matrix-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
}
.matrix-loader {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  width: 48px;
  height: 48px;
}
.matrix-cell {
  border-radius: 4px;
  animation: matrixPulse 1.2s ease-in-out infinite;
}
.cell-tl { background: #6C30CC; animation-delay: 0s; }
.cell-tr { background: #3AC47D; animation-delay: 0.15s; }
.cell-bl { background: #F9A825; animation-delay: 0.3s; }
.cell-br { background: #CC3030; animation-delay: 0.45s; }
@keyframes matrixPulse {
  0%, 100% { opacity: 0.25; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
}

/* Custom Tooltip */
.chart-tooltip {
  position: absolute;
  pointer-events: none;
  background: #ffffff;
  border: 1px solid #e8e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(108, 48, 204, 0.08), 0 4px 8px rgba(0, 0, 0, 0.03);
  padding: 12px 16px;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.15s ease;
  min-width: 180px;
}
.chart-tooltip.visible { opacity: 1; }

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.tooltip-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(108, 48, 204, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #6C30CC;
  flex-shrink: 0;
  overflow: hidden;
}
.tooltip-avatar img { width: 100%; height: 100%; object-fit: cover; }
.tooltip-name {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1px;
}
.tooltip-team {
  font-size: 11px;
  color: #8e8ea0;
}
.tooltip-metrics {
  display: flex;
  gap: 16px;
}
.tooltip-metric {
  display: flex;
  flex-direction: column;
}
.tooltip-metric-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8e8ea0;
}
.tooltip-metric-val {
  font-size: 16px;
  font-weight: 900;
  color: #6C30CC;
}

/* Legend */
.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  padding: 12px 0 4px;
  margin-bottom: 20px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #5a5a7a;
  padding: 3px 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
}
.legend-item:hover { background: #fafaff; }
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Quadrant Summary Pills */
.quadrant-summary {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}
.q-pill {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  cursor: default;
  min-width: 0;
}
.q-pill .q-count {
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
  flex-shrink: 0;
}
.pill-critical { background: rgba(183, 28, 28, 0.08); color: #b71c1c; }
.pill-danger { background: rgba(204, 48, 48, 0.06); color: #CC3030; }
.pill-warning { background: rgba(247, 185, 36, 0.07); color: #b8860b; }
.pill-success { background: rgba(58, 196, 125, 0.07); color: #1a8a4a; }
.pill-neutral { background: rgba(108, 48, 204, 0.06); color: #8e8ea0; }

/* Table Section */
.table-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.table-title {
  font-size: 14px;
  font-weight: 700;
  color: #2e2e2e;
}
.table-count {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

/* Table styling to match insights views */
.talent-table {
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015);
}

.emp-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}
.emp-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(108, 48, 204, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #6C30CC;
  flex-shrink: 0;
  overflow: hidden;
}
.emp-avatar img { width: 100%; height: 100%; object-fit: cover; }
.emp-info {
  min-width: 0;
}
.emp-name {
  font-size: 13px;
  font-weight: 600;
  color: #2e2e2e;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.emp-team-small {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.metric-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.network-badge,
.talent-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.quadrant-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
}
.qt-critical { background: rgba(183, 28, 28, 0.08); color: #b71c1c; }
.qt-danger { background: rgba(204, 48, 48, 0.06); color: #CC3030; }
.qt-warning { background: rgba(247, 185, 36, 0.07); color: #b8860b; }
.qt-success { background: rgba(58, 196, 125, 0.07); color: #1a8a4a; }
.qt-neutral { background: rgba(108, 48, 204, 0.06); color: #8e8ea0; }

/* Drawer Styles */
.drawer-content {
  height: 100%;
  overflow-y: auto;
}
.drawer-profile {
  padding: 28px 24px 20px;
  border-bottom: 1px solid #f0f0f6;
}
.drawer-profile-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.drawer-avatar-ring {
  flex-shrink: 0;
  padding: 3px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6C30CC 0%, #3AC47D 100%);
}
.drawer-avatar {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #f5f5fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: #6C30CC;
  overflow: hidden;
  border: 3px solid #fff;
}
.drawer-avatar img { width: 100%; height: 100%; object-fit: cover; }
.drawer-profile-info {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.drawer-name {
  font-size: 18px;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 2px;
  line-height: 1.3;
}
.drawer-meta {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
  margin-bottom: 10px;
}
.drawer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.drawer-section {
  padding: 18px 24px;
  border-bottom: 1px solid #f0f0f6;
}
.drawer-section:last-child { border-bottom: none; }
.drawer-section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #909399;
  margin-bottom: 12px;
}
.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
}
.metric-row + .metric-row { border-top: 1px solid #f5f5f8; }
.metric-label {
  font-size: 12px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
  min-width: 120px;
}
.metric-value {
  font-size: 13px;
  font-weight: 700;
  color: #2e2e2e;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
}
.metric-bar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  margin-left: 16px;
  max-width: 180px;
}
.metric-bar-track {
  flex: 1;
  height: 5px;
  background: #eeeef2;
  border-radius: 3px;
  overflow: hidden;
}
.metric-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: #6C30CC;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>

<style>
/* Unscoped Element UI table overrides for talent matrix */
.talent-table .el-table__row {
  cursor: pointer;
  transition: background-color 0.2s;
}
.talent-table .el-table__row:hover > td {
  background-color: #fafafa !important;
}
.talent-table .el-table__body-wrapper {
  overflow-y: auto;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #E0E0E0;
  border-radius: 4px;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.talent-table.el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafcff;
}
</style>
